import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { Car } from '@/api/cars/types';
import {
  NO_VIN_TRIM_MESSAGE,
  selectedTrimId,
  trimsForVin,
  useCarDetailOptions,
} from '@/api/partners/cars/use-car-detail-options';
import { camelCaseToTitleCase, snakeToCamelCase } from '@/core';
import { ControlledInput, Input, Text } from '@/ui';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import { Loader } from '@/ui/loader';

type CarDetailsFormProps = {
  // dailyRate: number;
  // oneDayMultiplier?: number;
  // pickupLocationId?: number;
};

const carDetailsFormSchema = z
  .object({
    licensePlate: z.string().min(1, 'License plate is required'),
    licensePlateState: z.string().min(1, 'State is required'),
    trimId: z.string().min(1, 'Trim is required'),
    autopilotType: z.string().optional(),
    color: z.string().min(1, 'Color is required'),
    numSeats: z.string(),
    gloveboxPin: z.string().min(4, 'PIN must be at least 4 digits'),
  })
  .superRefine((data, ctx) => {
    if (data.numSeats !== undefined && data.numSeats !== '') {
      const seats = Number(data.numSeats);

      if (Number.isNaN(seats)) {
        ctx.addIssue({
          path: ['numSeats'],
          message: 'Seating capacity must be a number',
          code: z.ZodIssueCode.custom,
        });
      } else if (seats < 5) {
        ctx.addIssue({
          path: ['numSeats'],
          message: 'Seating capacity must be at least 5',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

export type CarDetailsFormType = z.infer<typeof carDetailsFormSchema>;

export default function CarDetailsForm({
  car,
  name,
  onChange,
  onValidChange,
  onErrorsChange,
}: {
  car: Car;
  name: string;
  onChange: (name: string, values: CarDetailsFormType) => void;
  onValidChange: (name: string, isValid: boolean) => void;
  onErrorsChange: (name: string, errors: any) => void;
}) {
  const { styles, theme } = useStyles(stylesheet);

  const { data: carDetailOptions, isPending: isLoadingCarDetailOptions } =
    useCarDetailOptions({
      variables: { vins: [car.vin] },
    });

  const trimField = useMemo(() => {
    const resolved = trimsForVin({
      vin: car.vin,
      trims: carDetailOptions?.trims,
      vinTrims: carDetailOptions?.vinTrims,
      modelId: car.model?.id,
    });

    return {
      locked: resolved.locked,
      options: resolved.trims.map((trim) => ({
        id: trim.id.toString(),
        value: trim.id.toString(),
        title: trim.displayName,
      })),
    };
  }, [car.vin, car.model?.id, carDetailOptions]);

  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid, errors },
    watch,
    reset,
  } = useForm<CarDetailsFormType>({
    defaultValues: {
      licensePlate: car.licensePlate || '',
      licensePlateState:
        car.licensePlateState || ''
          ? carDetailOptions?.licensePlateStates[
              snakeToCamelCase(car.licensePlateState)
            ]?.toString()
          : '',
      trimId: selectedTrimId({
        options: trimField.options,
        locked: trimField.locked,
        currentTrimId: car.trim?.id.toString() || '',
      }),
      autopilotType:
        carDetailOptions?.autopilotTypes[
          car.autopilotType?.toLowerCase()
        ]?.toString() || '',
      color:
        carDetailOptions?.colors[car.color?.toLowerCase()]?.toString() || '',
      numSeats: car.numSeats?.toString() || '5',
      gloveboxPin: car.gloveboxPin || '',
    },
    resolver: zodResolver(carDetailsFormSchema),
  });

  useEffect(() => {
    if (carDetailOptions) {
      reset({
        licensePlate: car.licensePlate || '',
        licensePlateState: car.licensePlateState
          ? carDetailOptions?.licensePlateStates[
              snakeToCamelCase(car.licensePlateState)
            ]?.toString()
          : '',
        trimId: selectedTrimId({
          options: trimField.options,
          locked: trimField.locked,
          currentTrimId: car.trim?.id.toString() || '',
        }),
        autopilotType:
          carDetailOptions?.autopilotTypes[
            car.autopilotType?.toLowerCase?.()
          ]?.toString() || '',
        color: car.color
          ? carDetailOptions?.colors[car.color?.toLowerCase?.()]?.toString()
          : '',
        numSeats: car.numSeats?.toString() || '5',
        gloveboxPin: car.gloveboxPin || '',
      });
    }
  }, [
    car.autopilotType,
    car.color,
    car.gloveboxPin,
    car.licensePlate,
    car.licensePlateState,
    car.numSeats,
    car.trim?.id,
    carDetailOptions,
    reset,
    trimField,
  ]);

  const [licensePlateState, trimId, autopilotType, color] = useWatch({
    control,
    name: ['licensePlateState', 'trimId', 'autopilotType', 'color'],
  });

  useEffect(() => {
    onChange(name, getValues());
    trigger();
  }, []);

  useEffect(() => {
    onValidChange(name, isValid);
  }, [isValid]);

  useEffect(() => {
    setTimeout(() => {
      trigger();
    }, 500);
  }, []);

  useEffect(() => {
    onErrorsChange(name, errors);
  }, [JSON.stringify(errors)]);

  useEffect(() => {
    const sub = watch(() => {
      onChange(name, getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  if (isLoadingCarDetailOptions) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.colors.background_1 }}>
        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <View style={styles.registrationContainer}>
          <View style={{ gap: 16 }}>
            <Text type="title2">Registration</Text>

            <Input label="VIN" value={car.vin} disabled />

            <View style={styles.licenseContainer}>
              <ControlledInput
                control={control}
                name="licensePlate"
                label="License Plate"
                onBlur={() => {
                  trigger('licensePlate');
                }}
                onChangeText={(text) => {
                  const upperText = text.toUpperCase();
                  setValue('licensePlate', upperText);
                  trigger('licensePlate');
                }}
              />

              <Dropdown
                mode="single"
                label="License Plate State"
                options={
                  Object.keys(carDetailOptions?.licensePlateStates || {}).map(
                    (name) => ({
                      id: carDetailOptions?.licensePlateStates[name].toString(),
                      value:
                        carDetailOptions?.licensePlateStates[name].toString(),
                      title: camelCaseToTitleCase(name),
                    })
                  ) || []
                }
                placeHolderText="Select License State"
                onSelect={(selected: DropdownOption[]) => {
                  setValue('licensePlateState', selected[0].id);
                  trigger('licensePlateState');
                }}
                values={licensePlateState ? [licensePlateState] : []}
              />
            </View>
          </View>
        </View>

        <View style={styles.configurationPinContainer}>
          <View style={{ gap: 16 }}>
            <Text type="title2">Configuration</Text>

            <View style={styles.configurationInputsContainer}>
              <Dropdown
                mode="single"
                label="Trim"
                options={trimField.options}
                disabled={trimField.locked}
                placeHolderText="Performance, Long Range, etc."
                onSelect={(selected: DropdownOption[]) => {
                  setValue('trimId', selected[0].id);
                  trigger('trimId');
                }}
                values={trimId ? [trimId] : []}
                error={
                  trimField.options.length === 0
                    ? NO_VIN_TRIM_MESSAGE
                    : errors.trimId?.message
                }
                style={styles.configurationInput}
              />

              <Dropdown
                mode="single"
                label="Autopilot"
                options={
                  Object.keys(carDetailOptions?.autopilotTypes || {}).map(
                    (name) => ({
                      id: carDetailOptions?.autopilotTypes[name].toString(),
                      value: carDetailOptions?.autopilotTypes[name].toString(),
                      title: camelCaseToTitleCase(name),
                    })
                  ) || []
                }
                placeHolderText="Basic, Full self driving (FSD)"
                onSelect={(selected: DropdownOption[]) => {
                  setValue('autopilotType', selected[0].id);
                  trigger('autopilotType');
                }}
                values={autopilotType ? [autopilotType] : []}
                style={styles.configurationInput}
              />

              <Dropdown
                mode="single"
                label="Exterior Color"
                options={
                  Object.keys(carDetailOptions?.colors || {}).map((name) => ({
                    id: carDetailOptions?.colors[name].toString(),
                    value: carDetailOptions?.colors[name].toString(),
                    title: camelCaseToTitleCase(name),
                  })) || []
                }
                placeHolderText="Select exterior color"
                onSelect={(selected: DropdownOption[]) => {
                  setValue('color', selected[0].id);
                  trigger('color');
                }}
                values={color ? [color] : []}
                style={styles.configurationInput}
              />

              <ControlledInput
                control={control}
                name="numSeats"
                label="Seating capacity"
                keyboardType="number-pad"
                onBlur={() => {
                  trigger('numSeats');
                }}
                onChangeText={(text) => {
                  setValue('numSeats', text);
                  trigger('numSeats');
                }}
              />
            </View>
          </View>

          <View style={{ gap: 16 }}>
            <Text type="title2">PIN</Text>

            <ControlledInput
              control={control}
              name="gloveboxPin"
              label="Glovebox pin"
              keyboardType="number-pad"
              placeholder="Set your 4-digit PIN to Drive"
              onBlur={() => {
                trigger('gloveboxPin');
              }}
              onChangeText={(text) => {
                setValue('gloveboxPin', text);
                trigger('gloveboxPin');
              }}
              tooltipText="For added security, we require you to keep the key card in the glovebox and set a glovebox PIN."
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
  },
  contentContainer: {
    flexGrow: 1,
    gap: 24,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  },
  registrationContainer: {
    flexBasis: {
      md: '50%',
    },
    flexGrow: {
      md: 0,
    },
  },
  licenseContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: 16,
  },
  configurationPinContainer: {
    flexBasis: {
      md: '50%',
    },
    flexGrow: {
      md: 0,
    },
    gap: 32,
  },
  configurationInputsContainer: {
    flex: 1,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    flexWrap: {
      md: 'wrap',
    },
    gap: 16,
  },
  configurationInput: {
    flexBasis: {
      md: '48%',
    },
    flexGrow: {
      md: 1,
    },
    minWidth: 0,
  },
}));
